import noop from 'utils/noop';
import { TypeaheadSelect, TypeaheadBasicSelectProps } from 'ui/selects';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import React from 'react';
import { addNegativeToast } from 'modules/toastContainer';
import { FIELDS_QUERY_TYPE } from 'modules/workflowNodeEditors/components/fieldsMatchEditor/constants';
import { useFieldFacetTypeaheadOptions } from './hooks';
import { StyledTopTooltip } from './CriteriaPropertyField.styled';
import { MAX_JOURNEY_FIELD_LENGTH } from './constants';

const StyledTypeaheadSelect = styled<any>(TypeaheadSelect)`
  width: 100%;
`;

export const inputLengthError = (length: number) => `Character limit is ${length}`;
export const CRITERIA_TYPEAHEAD_FIELD_TEST_ID = 'criteria-text-input';

export interface CriteriaTypeaheadFieldProps
  extends Omit<TypeaheadBasicSelectProps<string>, 'options'> {
  /** If the text field is disabled. */
  disabled?: boolean;
  /** If the field has an error. */
  error?: boolean;
  /** Array comtaining the group index, query index and criteria property index. */
  fieldIdentifier: string[];
  /** If the field is dynamic. */
  isDynamic?: boolean;
  /** Callback function when field value is changed. */
  onChange?: (...args: string[]) => void;
  /** The value of the field. */
  value?: string;
  isReadOnly?: boolean;
  type?: string;
}

export const CriteriaTypeaheadField = ({
  className,
  disabled,
  value = '',
  fieldIdentifier = [],
  onChange = noop,
  error,
  isReadOnly = false,
  type,
  ...rest
}: CriteriaTypeaheadFieldProps) => {
  const isJourneyStudio = type === FIELDS_QUERY_TYPE;

  const dispatch = useDispatch();
  const options = useFieldFacetTypeaheadOptions();
  const [hasInputLengthError, setHasInputLengthError] = React.useState(
    isJourneyStudio && value?.length > MAX_JOURNEY_FIELD_LENGTH,
  );

  const handleChange = ([changeValue]: string[]) => {
    if (isJourneyStudio) {
      if (changeValue?.length > MAX_JOURNEY_FIELD_LENGTH) {
        dispatch(addNegativeToast(inputLengthError(MAX_JOURNEY_FIELD_LENGTH)));
        setHasInputLengthError(true);
      } else {
        setHasInputLengthError(false);
      }
    }
    onChange(changeValue, ...fieldIdentifier);
  };

  /**
   * TODO: add loading state once `TypeaheadSelect` supports it
   */

  return (
    <StyledTopTooltip
      defaultOpen={hasInputLengthError}
      isEnabled={hasInputLengthError || (isReadOnly && !!value)}
      tooltipContent={hasInputLengthError ? inputLengthError(MAX_JOURNEY_FIELD_LENGTH) : value}
    >
      <StyledTypeaheadSelect
        {...rest}
        className={className}
        id="criteria-typeahead"
        data-test={CRITERIA_TYPEAHEAD_FIELD_TEST_ID}
        defaultValues={[value]}
        onSelect={handleChange}
        options={options}
        isError={error || hasInputLengthError}
        isDisabled={disabled}
      />
    </StyledTopTooltip>
  );
};

export default CriteriaTypeaheadField;
